import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ReturnExceptionFeatureService } from '../../services/return-exception-feature.service';
import { ReturnExceptionVO } from '@app/api/order-service/model/returnExceptionVO';
import {
  GalleriaComponent,
  GalleriaConfig,
  GalleriaImage,
} from '@shared/components/galleria/galleria.component';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import { RETURN_EXCEPTION_STATUS } from '../../return-exception.config';

@Component({
  selector: 'app-return-exception-detail-dialog',
  standalone: true,
  imports: [CommonModule, TranslateModule, GalleriaComponent],
  templateUrl: './return-exception-detail-dialog.component.html',
  styleUrl: './return-exception-detail-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReturnExceptionDetailDialogComponent implements OnInit {
  private readonly dialogConfig = inject(DynamicDialogConfig);
  private readonly featureService = inject(ReturnExceptionFeatureService);
  private readonly translate = inject(TranslateService);

  loading = signal(true);
  detail = signal<ReturnExceptionVO | null>(null);

  galleryConfig: GalleriaConfig = {
    showThumbnails: true,
    showItemNavigators: true,
    numVisible: 5,
  };

  storeDisplay = computed(() => {
    const row = this.detail();
    if (!row) {
      return '';
    }
    const code = row.storeCode?.trim();
    const name = row.storeName?.trim();
    if (code && name && code !== name) {
      return `${code} - ${name}`;
    }
    return name ?? code ?? '';
  });

  galleryImages = computed((): GalleriaImage[] => {
    const urls = this.detail()?.imageUrls ?? [];
    return urls.map((src, index) => ({
      id: String(index),
      src,
      alt: this.translate.instant('return_exception.detail.image_alt', {
        index: index + 1,
      }),
      thumbnailSrc: src,
    }));
  });

  ngOnInit(): void {
    const id = Number(this.dialogConfig.data?.id);
    if (!id) {
      this.loading.set(false);
      return;
    }
    this.featureService.getById(id).subscribe({
      next: (vo) => {
        this.detail.set(vo);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }

  getStatusLabel(status?: string): string {
    switch (status) {
      case RETURN_EXCEPTION_STATUS.PENDING_APPROVAL:
        return this.translate.instant('return_exception.status.pending_approval');
      case RETURN_EXCEPTION_STATUS.APPROVED:
        return this.translate.instant('return_exception.status.approved');
      case RETURN_EXCEPTION_STATUS.INVALID:
        return this.translate.instant('return_exception.status.invalid');
      default:
        return status ?? '';
    }
  }

  formatDateTime(value?: string): string {
    return value ? formatDateTime(value) : '';
  }
}
